import {
  useGetCompany,
  useUpsertCompany,
  useUpsertGroupAttributes,
  useDeleteGroupAttributes,
  useGetGroupAttributes,
} from '@dispatch/shared/hooks';
import { CompanyAdminDetails } from '@dispatch/web/features/company-admin';
import { PageComponent } from '@dispatch/web/ui';
import React from 'react';
import { useQueryClient } from 'react-query';
import { AddEditModal } from './components/modals';
import {
  Company,
  CompanyAdminMeta,
  CompanyGroupAttributeValuesVarchars,
} from '@dispatch/shared/types';
import {
  CompanyGroupAttributes,
  GroupAttributeMatchMode,
} from '@dispatch/shared/types';
import { useNotification } from '@dispatch/web/hooks';
import { useAccessToken, useCurrentUser } from '@dispatch/web/auth';
import { useIsAuthenticated } from '@azure/msal-react';

export const CompanyAdminPage = () => {
  const defaultValues = {
    addAttribute: { open: false, data: null },
    addGroupAttribute: { open: false, data: null },
    delete: { open: false, data: null },
    addResourceRole: { open: false, data: null },
    addOffice: { open: false, data: null },
    addTeam: { open: false, data: null },
    addGroup: { open: false, data: null },
    addCertAndLicense: { open: false, data: null },
  };
  const queryClient = useQueryClient();
  const { companyData, companyStatus } = useGetCompany();

  const { tenantId } = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();
  const { accessToken } = useAccessToken();
  const { groupAttributesData, groupAttributesRefetch } = useGetGroupAttributes(
    tenantId,
    !!tenantId,
    60000
  );
  const { notifySuccess, notifyError } = useNotification();
  const [meta, setmeta] = React.useState<CompanyAdminMeta>(defaultValues);

  const [groupAttributes, setGroupAttributes] = React.useState<
    CompanyGroupAttributes[]
  >([]);

  // Refetch group attributes when access token changes (token refresh)
  React.useEffect(() => {
    if (isAuthenticated && tenantId && accessToken.token) {
      groupAttributesRefetch();
    }
  }, [accessToken.token, isAuthenticated, tenantId, groupAttributesRefetch]);

  // Update local state when backend data changes
  React.useEffect(() => {
    if (groupAttributesData) {
      // Transform API response to match CompanyGroupAttributes format
      const transformedData = groupAttributesData.map(
        (apiData: {
          id: string;
          attributeGroupName: string;
          groupMap?: Record<string, Record<string, string>>;
          valueMatchModes?: Record<string, GroupAttributeMatchMode>;
        }) => {
          // Transform groupMap back to selectedAttributeValuesVarchars format
          const selectedAttributeValuesVarchars: CompanyGroupAttributeValuesVarchars[] =
            [];

          if (apiData.groupMap) {
            Object.entries(apiData.groupMap).forEach(
              ([attributeGUID, attributeValues]: [
                string,
                Record<string, string>
              ]) => {
                Object.entries(attributeValues).forEach(
                  ([valueGUID, displayName]) => {
                    selectedAttributeValuesVarchars.push({
                      selectedAttributeDefinitionIntegrationGUID: attributeGUID,
                      attributeDefinitionValueIntegrationGUID: valueGUID,
                      title: displayName as string,
                      value: displayName as string,
                      displayName: displayName as string,
                    });
                  }
                );
              }
            );
          }

          return {
            integrationGUID: apiData.id,
            displayName: apiData.attributeGroupName,
            attributeDefinitionIntegrationGUID: apiData.id,
            selectedAttributeValuesVarchars: selectedAttributeValuesVarchars,
            valueMatchModes: apiData.valueMatchModes,
          };
        }
      );
      setGroupAttributes(transformedData);
    }
  }, [groupAttributesData]);

  const onSuccess = (response: { success: boolean; message?: string }) => {
    if (response?.success) {
      notifySuccess('Success', 'Company Updated');
    } else {
      notifyError('Error', 'Failed to update company');
    }
    setmeta(defaultValues);
  };

  const onGroupAttributeSuccess = (response: {
    success: boolean;
    message?: string;
  }) => {
    if (response?.success) {
      notifySuccess('Success', 'Group Attribute Saved');
      queryClient.invalidateQueries(['groupAttributes']);
    } else {
      notifyError('Error', 'Failed to save group attribute');
    }
    setmeta(defaultValues);
  };

  const onGroupAttributeDeleteSuccess = (response: {
    success: boolean;
    message?: string;
  }) => {
    if (response?.success) {
      notifySuccess('Success', 'Group Attribute Deleted');
      queryClient.invalidateQueries(['groupAttributes']);
    } else {
      notifyError('Error', 'Failed to delete group attribute');
    }
    setmeta(defaultValues);
  };

  const companyApi = useUpsertCompany(onSuccess);
  const groupAttributesApi = useUpsertGroupAttributes(
    onGroupAttributeSuccess,
    tenantId
  );
  const deleteGroupAttributesApi = useDeleteGroupAttributes(
    onGroupAttributeDeleteSuccess,
    tenantId
  );

  // Transform group attribute data to API format
  const createGroupAttributeApiPayload = (formData: {
    integrationGUID: string;
    displayName: string;
    tenantId: string;
    docType: string;
    attributes?: {
      integrationGUID: string;
      displayName: string;
      attributeDefinitionIntegrationGUID: string;
      matchMode?: GroupAttributeMatchMode;
      selectedAttributeValuesVarchars: CompanyGroupAttributeValuesVarchars[];
    }[];
  }) => {
    const nestedAttributes: Record<string, Record<string, string>> = {};
    const valueMatchModes: Record<string, GroupAttributeMatchMode> = {};

    formData.attributes?.forEach((attr) => {
      const attributeGUID = attr.attributeDefinitionIntegrationGUID;
      if (!nestedAttributes[attributeGUID]) {
        nestedAttributes[attributeGUID] = {};
      }

      valueMatchModes[attributeGUID] =
        attr.matchMode ?? GroupAttributeMatchMode.ANY;

      attr.selectedAttributeValuesVarchars?.forEach((value) => {
        const valueGUID = value.attributeDefinitionValueIntegrationGUID;
        const displayName = value.title || value.displayName || '';
        nestedAttributes[attributeGUID][valueGUID] = displayName;
      });
    });

    return {
      id: formData.integrationGUID,
      attributeGroupName: formData.displayName,
      tenantId: formData.tenantId,
      docType: 'AttributeGroupDocument',
      groupMap: nestedAttributes,
      valueMatchModes,
    };
  };

  const handleSave = React.useCallback(
    (body: unknown & { integrationGUID: string }, key?: string) => {
      if (key === 'groupAttribute') {
        const formData = body as {
          integrationGUID: string;
          displayName: string;
          tenantId: string;
          docType: string;
          attributes?: {
            integrationGUID: string;
            displayName: string;
            attributeDefinitionIntegrationGUID: string;
            matchMode?: GroupAttributeMatchMode;
            selectedAttributeValuesVarchars: CompanyGroupAttributeValuesVarchars[];
          }[];
        };

        // Transform form data to CompanyGroupAttributes format
        // Flatten all selected attribute values into a single array
        const allSelectedValues =
          formData.attributes?.flatMap(
            (attr) =>
              attr.selectedAttributeValuesVarchars?.map((value) => ({
                integrationGUID:
                  value.selectedAttributeDefinitionIntegrationGUID || '',
                selectedAttributeDefinitionIntegrationGUID:
                  attr.attributeDefinitionIntegrationGUID,
                attributeDefinitionValueIntegrationGUID:
                  value.attributeDefinitionValueIntegrationGUID,
                title: value.title, // This should be the actual value name like "Fire", "Water"
                value: value.value,
                displayName: value.title, // Use the actual selected value name, not attribute type name
              })) || []
          ) || [];

        const groupAttribute: CompanyGroupAttributes = {
          integrationGUID: formData.integrationGUID,
          displayName: formData.displayName,
          attributeDefinitionIntegrationGUID: formData.integrationGUID,
          selectedAttributeValuesVarchars: allSelectedValues,
        };

        // Create API payload in the new nested format for backend (using attributeGroupName)
        const apiPayload = createGroupAttributeApiPayload(formData);

        // Use upsert for both create and edit operations
        groupAttributesApi.upsertGroupAttributes(apiPayload);

        setGroupAttributes((prev) => {
          const existing = prev.find(
            (item) => item.integrationGUID === groupAttribute.integrationGUID
          );
          if (existing) {
            // Update existing
            const updated = prev.map((item) =>
              item.integrationGUID === groupAttribute.integrationGUID
                ? groupAttribute
                : item
            );
            return updated;
          } else {
            // Add new
            const newState = [...prev, groupAttribute];
            return newState;
          }
        });
        return;
      }

      // Handle other entities with backend calls
      const dataObject: unknown[] = companyData?.[
        key as keyof typeof companyData
      ] as unknown[];
      let updated = false;
      const update = dataObject?.flatMap((item: unknown) => {
        const itemIntegrationGUID = (item as { integrationGUID: string })
          .integrationGUID;
        if (itemIntegrationGUID === body.integrationGUID) {
          updated = true;
          return body;
        }
        return item;
      });
      if (!updated) update?.push(body);

      const updatedCompany: Partial<Company> = {
        ...companyData,
        [key as keyof typeof companyData]: update,
      };

      companyApi.upsertCompany(updatedCompany);
    },
    [companyData, companyApi, groupAttributesApi, setGroupAttributes]
  );

  const handleDelete = React.useCallback(
    (type: string, integrationGUID: string) => {
      if (type === 'groupAttribute') {
        deleteGroupAttributesApi.deleteGroupAttributes(integrationGUID);
        return;
      }

      // Handle other entities with backend calls
      const currentObj = companyData?.[type as keyof typeof companyData] as
        | { integrationGUID: string }[]
        | undefined;
      const newOb = currentObj?.filter(
        (item) => item.integrationGUID !== integrationGUID
      );
      const updatedCompany: Partial<Company> = {
        ...companyData,
        [type as keyof typeof companyData]: newOb,
      };

      companyApi.upsertCompany(updatedCompany);
    },
    [companyData, companyApi, deleteGroupAttributesApi]
  );

  const handleCompanySettingsSave = React.useCallback(
    (data: Record<string, string>) => {
      const updatedCompany: Partial<Company> = {
        ...companyData,
        settings: {
          ...data,
        },
      };

      companyApi.upsertCompany(updatedCompany);
    },
    [companyApi, companyData]
  );

  return (
    <PageComponent title="Edit Company">
      <>
        <CompanyAdminDetails
          company={companyData}
          groupAttributes={groupAttributes}
          modalState={{ meta, setmeta }}
          isLoading={companyStatus === 'loading'}
          companyUpsertStatus={companyApi.status}
          handleCompanySettingsSave={handleCompanySettingsSave}
        />
        <AddEditModal
          modal={meta}
          setModal={setmeta}
          handleSave={handleSave}
          handleDelete={handleDelete}
          companyData={companyData}
          groupAttributes={groupAttributes}
        />
      </>
    </PageComponent>
  );
};
